import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import Layout from "@/components/Layout";
import { Trophy, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { UpdatedAgo } from "@/components/LiveTop5Board";
import usePageMeta from "@/hooks/usePageMeta";

const SOURCE_LABEL = { live: "Live stats", simulated: "Simulated", projected: "Pre-kickoff" };

export default function Entries() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const hasLive = useRef(false);

  usePageMeta({
    title: "My Entries · PickemSports.club",
    description: "Your challenge history, scores, ranks and top-five finishes.",
    path: "/entries",
    noindex: true,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/users/me/entries");
        if (cancelled) return;
        setEntries(data);
        hasLive.current = data.some((e) => e.is_live);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    // Keep in-flight slates ticking without hammering the API when nothing is live
    const t = setInterval(() => { if (hasLive.current) load(); }, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-2">History</div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight">MY ENTRIES</h1>
        <p className="text-white/40 text-sm mt-2">Tap any entry to open its live board.</p>
      </div>

      {loading ? (
        <div className="text-white/40 text-center py-20 font-display tracking-widest text-2xl animate-pulse">
          LOADING...
        </div>
      ) : entries.length === 0 ? (
        <div className="border border-white/10 bg-[#141414] p-12 text-center">
          <Trophy size={40} className="text-white/20 mx-auto mb-4" />
          <div className="font-display text-2xl mb-2">NO ENTRIES YET</div>
          <p className="text-white/50 text-sm mb-6">Enter your first contest to start earning XP.</p>
          <Link
            to="/lobby"
            data-testid="empty-cta-lobby"
            className="bg-[#00FF66] hover:bg-[#00e65b] text-black font-bold uppercase tracking-widest px-5 py-2.5 text-sm rounded-sm inline-flex items-center gap-2"
          >
            Browse Contests
          </Link>
        </div>
      ) : (
        <div className="space-y-2" data-testid="entries-list">
          {entries.map((e) => {
            return (
              <Link
                key={e.entry_id}
                to={`/contest/${e.contest_id}`}
                data-testid={`entry-row-${e.entry_id}`}
                className="block border border-white/10 bg-[#141414] hover:border-[#00FF66]/40 p-4 transition-colors"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-semibold flex items-center gap-2 flex-wrap">
                      {e.contest_title}
                      {e.is_live && (
                        <span
                          className="text-[10px] uppercase tracking-widest text-[#00FF66] border border-[#00FF66]/40 bg-[#00FF66]/10 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1"
                          data-testid={`entry-live-badge-${e.entry_id}`}
                        >
                          <span className="w-1.5 h-1.5 bg-[#00FF66] rounded-full animate-pulse" /> Live
                        </span>
                      )}
                    </div>
                    <div className="text-white/40 text-[10px] uppercase tracking-widest mt-0.5">
                      {e.contest_position} · {e.contest_status}
                      {e.is_live && (
                        <>
                          {" · "}{SOURCE_LABEL[e.live_source] || "Pre-kickoff"}{" · "}
                          <UpdatedAgo at={e.live_updated_at} testId={`entry-updated-${e.entry_id}`} />
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-5 text-right">
                    <div>
                      <div className="text-white/40 text-[10px] uppercase tracking-widest">Entry</div>
                      <div className="font-display text-lg">FREE</div>
                    </div>
                    <div>
                      <div className="text-white/40 text-[10px] uppercase tracking-widest">Score</div>
                      <div className="font-display text-lg" data-testid={`entry-score-${e.entry_id}`}>
                        {e.score || 0}
                      </div>
                    </div>
                    <div>
                      <div className="text-white/40 text-[10px] uppercase tracking-widest">Rank</div>
                      <div className="font-display text-lg" data-testid={`entry-rank-${e.entry_id}`}>
                        {e.rank ? `#${e.rank}` : "—"}
                        {e.contest_field ? <span className="text-white/30 text-sm">/{e.contest_field}</span> : null}
                      </div>
                    </div>
                    <div>
                      <div className="text-white/40 text-[10px] uppercase tracking-widest">Result</div>
                      <div
                        className={`font-display text-lg ${e.rank && e.rank <= 5 ? "text-[#00FF66]" : "text-white/30"}`}
                        data-testid={`entry-result-${e.entry_id}`}
                      >
                        {e.rank && e.rank <= 5 ? `TOP ${e.rank}` : "—"}
                      </div>
                    </div>
                    <ArrowRight size={16} className="text-white/30" />
                  </div>
                </div>

                {e.picks_detail?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {e.picks_detail.map((p) => (
                      <span
                        key={p.player_id}
                        data-testid={`entry-pick-${e.entry_id}-${p.player_id}`}
                        className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${
                          p.in_top5
                            ? "border-[#00FF66]/50 text-[#00FF66] bg-[#00FF66]/10"
                            : "border-white/10 text-white/40"
                        }`}
                      >
                        {p.slot}. {p.name}
                        {p.points != null && <span className="ml-1 normal-case">{p.points.toFixed(1)}</span>}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
